import React from 'react';
import { styled } from 'baseui';
import { MonoLabelXSmall } from 'baseui/typography';
import { useAtomValue } from 'jotai';
import { annotationIdToLabelMapAtom, annotationMetadataAtom } from '../../../../../labelling-v2/atoms/annotation-atoms';
import { FRAME_HEIGHT, getColumnHeaderHeight, ROW_HEADER_WIDTH, ROW_HEADER_Z_INDEX } from './constants';
import type { ScrollGutterSizeT } from './types';
type PropsT = {
  annotationObjectIds: string[];
  containerStripRef: React.RefObject<HTMLDivElement>;
  scrollGutterSize: ScrollGutterSizeT;
};
const MAX_ID_LENGTH = 8;
const Container = styled<'div', {
  $scrollGutterSize: ScrollGutterSizeT;
}>('div', ({
  $theme,
  $scrollGutterSize
}) => ({
  position: 'absolute',
  top: getColumnHeaderHeight($theme),
  bottom: `${$scrollGutterSize.bottom}px`,
  left: 0,
  overflow: 'hidden',
  backgroundColor: $theme.colors.backgroundPrimary,
  zIndex: ROW_HEADER_Z_INDEX
}));
const LabelId = styled(MonoLabelXSmall, {
  flexShrink: 0
});
const LabelText = styled(MonoLabelXSmall, {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  flexGrow: 1,
  minWidth: 0,
  textAlign: 'right'
});
const ContainerStrip = styled('div', {
  display: 'flex',
  flexDirection: 'column'
});
const LabelContainer = styled('div', ({
  $theme
}) => ({
  width: `${ROW_HEADER_WIDTH}px`,
  height: `${FRAME_HEIGHT}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: `0 ${$theme.sizing.scale200}`,
  boxSizing: 'border-box',
  borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
  borderRight: `1px solid ${$theme.colors.borderOpaque}`,
  userSelect: 'none'
}));
function RowHeadersInternal({
  annotationObjectIds,
  containerStripRef,
  scrollGutterSize
}: PropsT) {
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationIdToLabelMap = useAtomValue(annotationIdToLabelMapAtom);
  return <Container $scrollGutterSize={scrollGutterSize}>
      <ContainerStrip ref={containerStripRef}>
        {annotationObjectIds.map(id => {
        const annotationIdSmall = id.slice(-MAX_ID_LENGTH);
        const label = annotationIdToLabelMap.get(id);
        const meta = annotationMetadata.get(id);
        const strokeColor = label?.style.strokeColor ?? '';
        const annotationLabel = label?.title ?? meta?.labelId ?? '';
        return <LabelContainer key={id}>
              <LabelText color={strokeColor}>{annotationLabel}</LabelText>
              <LabelId color={strokeColor}>[{annotationIdSmall}]</LabelId>
            </LabelContainer>;
      })}
      </ContainerStrip>
    </Container>;
}
export const RowHeaders = React.memo(RowHeadersInternal);